import React, { useMemo, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Button } from '@resurs/midas-react'
import PageWrapper from '../../../../internal/PageWrapper'
import { getText } from '../../../../internal/TextLabels'
import QRCodeLink from '../../../../components/common/QRCodeLink/QRCodeLink'
import QRModal from '../../../../components/common/QRModal/QRModal'
import CurrencyFormat from '../../../../components/common/CurrencyFormat'
import style from './PushOutDonePage.module.scss'

const getTranslation = label => getText(`payApply.pushToCustomerDone.${label}`)

const unwrapAutomaticDebit = payment =>
  payment?.paymentResult?.publicSettings?.automaticDebit

const PushOutDonePage = ({
  data: { current, redirectionResult, payment },
  logout,
  hasCoApplicant,
}) => {
  const [showModal, setShowModal] = useState(false)
  const [isApplicantActive, setIsApplicantActive] = useState(true)
  const { selectedLimit, selectedAmount } = current
  const isPayment = !!selectedAmount
  const isApplication = !selectedAmount
  const { customer, coApplicant } = redirectionResult
  const isAutomaticDebit = useMemo(
    () => !!unwrapAutomaticDebit(payment),
    [unwrapAutomaticDebit(payment)],
  )

  const toggleModal = () => setShowModal(!showModal)

  const getModalTitle = useMemo(() => {
    if (isApplicantActive && hasCoApplicant) {
      return getText('payApply.pushToCustomer.linkApplicant')
    }
    if (hasCoApplicant) {
      return getText('payApply.pushToCustomer.linkCoApplicant')
    }
    return getText('payApply.pushToCustomer.link')
  }, [isApplicantActive])

  const onNext = () => {
    if (isApplicantActive) {
      return setIsApplicantActive(false)
    }
    toggleModal()
    return setIsApplicantActive(true)
  }

  const getDebitInstructions = useMemo(
    () =>
      isAutomaticDebit ? (
        <div>
          {getTranslation('instructions.automaticDebit.1')}
          <NavLink to="/transactions">
            {getTranslation('instructions.automaticDebit.2')}
          </NavLink>
          {getTranslation('instructions.automaticDebit.3')}
          <NavLink to="/payments/ongoing">
            {getTranslation('instructions.automaticDebit.4')}
          </NavLink>
          {getTranslation('instructions.automaticDebit.5')}
        </div>
      ) : (
        <div>
          {getTranslation('instructions.manualDebit.1')}
          <NavLink to="/payments/ongoing">
            {getTranslation('instructions.manualDebit.2')}
          </NavLink>
          {getTranslation('instructions.manualDebit.3')}
        </div>
      ),
    [isAutomaticDebit],
  )

  return (
    <>
      <PageWrapper type="success">
        <div className={style.container}>
          <div className={style.header}>
            <div className={style.leftSide}>
              <div className={style.successAnimation} />
              <h4>{getTranslation('title')}</h4>
            </div>
            <QRCodeLink
              description={getTranslation('description')}
              onClick={toggleModal}
            />
          </div>
          <div className={style.content}>
            <div className={style.applicationInformation}>
              {(isApplication || isPayment) && (
                <div className={style.creditInformation}>
                  <div>{getTranslation('requestedCreditLimit')}</div>
                  <CurrencyFormat value={selectedLimit} hideDecimalIfInteger />
                </div>
              )}
              {isPayment && (
                <div className={style.purchaseInformation}>
                  <div>{getTranslation('amount')}</div>
                  <CurrencyFormat value={selectedAmount} hideDecimalIfInteger />
                </div>
              )}
            </div>
            <div className={style.textArea}>
              <div>
                {getTranslation('instructions.merchant.1')}
                <NavLink to="/apply">
                  {getTranslation('instructions.merchant.2')}
                </NavLink>
                {getTranslation('instructions.merchant.3')}
              </div>
              {isPayment && getDebitInstructions}
              {getTranslation('instructions.other.1')}
            </div>
          </div>
        </div>
      </PageWrapper>
      <PageWrapper>
        <div className={style.navigation}>
          <NavLink to="/apply">
            <Button>{getText('payApply.restart')}</Button>
          </NavLink>
          <Button type="primary" onClick={logout}>
            {getText('payApply.logout')}
          </Button>
        </div>
      </PageWrapper>
      {customer?.url && (
        <QRModal
          title={getModalTitle}
          description={getText('payApply.pushToCustomer.scanQR')}
          show={showModal}
          showNext={hasCoApplicant}
          value={isApplicantActive ? customer?.url : coApplicant?.url}
          onClose={toggleModal}
          onNext={onNext}
        />
      )}
    </>
  )
}

export default PushOutDonePage
